import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { EMPTY_CALL_GUIDE, type CallGuide } from "./callScript";
import { usePatchQueue } from "./usePatchQueue";

export function useCallGuide(caseId: string) {
  const [guide, setGuide] = useState<CallGuide>(EMPTY_CALL_GUIDE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [revision, setRevision] = useState(0);
  const save = usePatchQueue<CallGuide>(async (patch) => {
    const { error } = await supabase
      .from("case_call_guides")
      .upsert({ case_id: caseId, ...patch }, { onConflict: "case_id" })
      .select("case_id")
      .single();
    if (error) throw error;
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from("case_call_guides")
          .select("*")
          .eq("case_id", caseId)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        setGuide({ ...EMPTY_CALL_GUIDE, ...data, ...save.unsaved() });
        setLoadError(false);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const channel = supabase
      .channel(`call-guide:${caseId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "case_call_guides", filter: `case_id=eq.${caseId}` },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [caseId, revision]);
  const change = useCallback(
    (patch: Partial<CallGuide>) => {
      setGuide((current) => ({ ...current, ...patch }));
      save.enqueue(patch);
    },
    [save.enqueue],
  );
  return { guide, change, loading, loadError, reload: () => setRevision((n) => n + 1), save };
}
